import { Controller } from "@hotwired/stimulus"

// Fetches imported events' images one request at a time, so each download gets a request of its own
// and the server only ever handles one at once. Shows progress as it goes.
export default class extends Controller {
  static targets = ["item", "bar", "progress", "summary", "startButton", "pauseButton", "retryButton"]
  static values = { autostart: Boolean }

  connect() {
    if (this.autostartValue) this.start()
  }

  disconnect() {
    this.paused = true
  }

  async start() {
    if (this.running) return

    this.running = true
    this.paused = false
    this.render()

    for (const item of this.itemTargets) {
      if (this.paused) break
      if (item.dataset.state !== "pending") continue

      await this.fetchImage(item)
      this.render()
    }

    this.running = false
    this.render()
  }

  pause() {
    this.paused = true
    this.render()
  }

  retry() {
    this.itemTargets.filter(item => item.dataset.state === "failed").forEach(item => this.setState(item, "pending", "Waiting"))
    this.start()
  }

  async fetchImage(item) {
    this.setState(item, "working", "Fetching…")

    try {
      const response = await fetch(item.dataset.url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok && data.attached) {
        this.setState(item, "done", "Added")
        if (data.preview_url) item.querySelector("[data-role='thumb']")?.setAttribute("src", data.preview_url)
      } else {
        this.setState(item, "failed", data.error || `Failed (${response.status})`)
      }
    } catch {
      this.setState(item, "failed", "Network error")
    }
  }

  setState(item, state, label) {
    item.dataset.state = state

    const status = item.querySelector("[data-role='status']")
    status.textContent = label
    status.title = label
    status.classList.toggle("text-stone-400", state === "pending")
    status.classList.toggle("text-foreground", state === "working")
    status.classList.toggle("text-emerald-700", state === "done")
    status.classList.toggle("text-red-700", state === "failed")
  }

  render() {
    const total = this.itemTargets.length
    const count = state => this.itemTargets.filter(item => item.dataset.state === state).length
    const done = count("done")
    const failed = count("failed")
    const left = count("pending") + count("working")

    this.barTarget.style.width = `${total ? ((done + failed) / total) * 100 : 0}%`
    this.progressTarget.setAttribute("aria-valuenow", done + failed)

    if (this.running && !this.paused) {
      this.summaryTarget.textContent = `Fetching images: ${done + failed} of ${total} done. Keep this page open until it's finished.`
    } else if (left > 0) {
      this.summaryTarget.textContent = `Paused with ${left} still to fetch.`
    } else {
      this.summaryTarget.textContent = `${done} of ${total} images added${failed ? `; ${failed} couldn't be fetched (upload those on the event page)` : ""}.`
    }

    this.pauseButtonTarget.hidden = !this.running || this.paused
    this.startButtonTarget.hidden = this.running || left === 0
    this.retryButtonTarget.hidden = this.running || failed === 0
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
